import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL } from "../../config/api";
import { upsertRealmDocs, realmAvailable } from "../../realm";
import {
  loadLocalSavedMessages,
  paginateSavedMessages,
  savedChannelsSummaryFromLocal,
  searchLocalSavedMessages,
} from "../../realm/offlineLookups";

// Async thunks
export const saveMessage = createAsyncThunk(
  "savedMessages/saveMessage",
  async (messageId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/saved-messages`,
        { messageId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to save message"
      );
    }
  }
);

export const unsaveMessage = createAsyncThunk(
  "savedMessages/unsaveMessage",
  async (messageId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/saved-messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return messageId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to unsave message"
      );
    }
  }
);

export const fetchSavedMessages = createAsyncThunk(
  "savedMessages/fetchSavedMessages",
  async (
    { page = 0, size = SAVED_MESSAGES_PAGE_SIZE, append = false } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      if (page === 0 && !append) {
        const cached = await loadLocalSavedMessages(getState);
        if (cached && cached.length) {
          return {
            ...paginateSavedMessages(cached, 0, size),
            append: false,
            fromRealm: true,
          };
        }
        if (await realmAvailable()) {
          return {
            ...paginateSavedMessages([], 0, size),
            append: false,
            fromRealm: true,
          };
        }
      }

      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/saved-messages`, {
        params: { page, size },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      const list = data.content || [];
      upsertRealmDocs("SavedMessage", list);
      return {
        savedMessages: list,
        page: data.page ?? page,
        hasMore: data.hasMore ?? false,
        totalElements: data.totalElements ?? list.length,
        append,
      };
    } catch (error) {
      const cached = await loadLocalSavedMessages(getState);
      if (cached && cached.length) {
        return {
          ...paginateSavedMessages(cached, page, size),
          append: false,
          fromRealm: true,
        };
      }
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch saved messages"
      );
    }
  }
);

export const fetchChannelsSummary = createAsyncThunk(
  "savedMessages/fetchChannelsSummary",
  async (_, { getState }) => {
    if (await realmAvailable()) {
      const local = await loadLocalSavedMessages(getState);
      return savedChannelsSummaryFromLocal(local);
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/saved-messages/channels-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      const local = await loadLocalSavedMessages(getState);
      return savedChannelsSummaryFromLocal(local);
    }
  }
);

export const searchSavedMessages = createAsyncThunk(
  "savedMessages/searchSavedMessages",
  async (
    {
      query,
      channelId = null,
      page = 0,
      size = SAVED_MESSAGES_PAGE_SIZE,
      append = false,
    },
    { getState }
  ) => {
    if (await realmAvailable()) {
      const local = await loadLocalSavedMessages(getState);
      const matched = searchLocalSavedMessages(local, query, channelId);
      return {
        query,
        channelId,
        ...paginateSavedMessages(matched, page, size),
        append,
        fromRealm: true,
      };
    }
    try {
      const token = localStorage.getItem("token");
      const params = { q: query, page, size };
      if (channelId) {
        params.channelId = channelId;
      }
      const response = await axios.get(`${API_URL}/saved-messages/search`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      return {
        query,
        channelId,
        savedMessages: data.content || [],
        page: data.page ?? page,
        hasMore: data.hasMore ?? false,
        totalElements: data.totalElements ?? (data.content || []).length,
        append,
      };
    } catch (error) {
      const local = await loadLocalSavedMessages(getState);
      const matched = searchLocalSavedMessages(local, query, channelId);
      return {
        query,
        channelId,
        ...paginateSavedMessages(matched, page, size),
        append,
        fromRealm: true,
      };
    }
  }
);

export const fetchAllSavedMessages = createAsyncThunk(
  "savedMessages/fetchAllSavedMessages",
  async (_, { getState, rejectWithValue }) => {
    if (await realmAvailable()) {
      return loadLocalSavedMessages(getState);
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/saved-messages/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      const local = await loadLocalSavedMessages(getState);
      if (local.length) return local;
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch saved messages"
      );
    }
  }
);

export const SAVED_MESSAGES_PAGE_SIZE = 20;

export const fetchSavedMessagesByChannel = createAsyncThunk(
  "savedMessages/fetchSavedMessagesByChannel",
  async (
    { channelId, page = 0, size = SAVED_MESSAGES_PAGE_SIZE, append = false },
    { getState }
  ) => {
    if (page === 0 && !append) {
      const local = await loadLocalSavedMessages(getState);
      const filtered = local.filter((sm) => sm.channelId === channelId);
      if (filtered.length || (await realmAvailable())) {
        return {
          channelId,
          ...paginateSavedMessages(filtered, 0, size),
          append: false,
          fromRealm: true,
        };
      }
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/saved-messages/channel/${channelId}`,
        {
          params: { page, size },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = response.data;
      return {
        channelId,
        savedMessages: data.content || [],
        page: data.page ?? page,
        hasMore: data.hasMore ?? false,
        totalElements: data.totalElements ?? (data.content || []).length,
        append,
      };
    } catch (error) {
      const local = await loadLocalSavedMessages(getState);
      const filtered = local.filter((sm) => sm.channelId === channelId);
      return {
        channelId,
        ...paginateSavedMessages(filtered, page, size),
        append,
        fromRealm: true,
      };
    }
  }
);

export const checkIfMessageSaved = createAsyncThunk(
  "savedMessages/checkIfMessageSaved",
  async (messageId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/saved-messages/${messageId}/check`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return { messageId, isSaved: response.data.isSaved };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to check saved status"
      );
    }
  }
);

const initialState = {
  savedMessages: [],
  savedMessagesByChannel: {},
  channelMetaByChannel: {},
  channelsSummary: [],
  listMeta: {
    page: 0,
    hasMore: false,
    totalElements: 0,
  },
  searchResults: [],
  searchMeta: {
    query: "",
    channelId: null,
    page: 0,
    hasMore: false,
    totalElements: 0,
  },
  savedMessageIds: [],
  channelLoading: false,
  channelLoadingMore: false,
  loading: false,
  loadingMore: false,
  searchLoading: false,
  searchLoadingMore: false,
  error: null,
};

const savedMessageSlice = createSlice({
  name: "savedMessages",
  initialState,
  reducers: {
    clearSavedMessages: (state) => {
      state.savedMessages = [];
      state.savedMessageIds = [];
    },
    addSavedMessageId: (state, action) => {
      if (!state.savedMessageIds.includes(action.payload)) {
        state.savedMessageIds.push(action.payload);
      }
    },
    removeSavedMessageId: (state, action) => {
      state.savedMessageIds = state.savedMessageIds.filter(
        (id) => id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Save message
      .addCase(saveMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveMessage.fulfilled, (state, action) => {
        state.loading = false;
        const savedMessage = action.payload;
        const exists = state.savedMessages.some((sm) => sm.id === savedMessage.id);
        if (!exists) {
          state.savedMessages.unshift(savedMessage);
        }
        if (!state.savedMessageIds.includes(savedMessage.messageId)) {
          state.savedMessageIds.push(savedMessage.messageId);
        }
        const channelId = savedMessage.channelId;
        if (channelId) {
          const channelList = state.savedMessagesByChannel[channelId] || [];
          const channelExists = channelList.some(
            (sm) => sm.messageId === savedMessage.messageId
          );
          if (!channelExists) {
            state.savedMessagesByChannel[channelId] = [
              savedMessage,
              ...channelList,
            ];
          }
          const meta = state.channelMetaByChannel[channelId] || {
            page: 0,
            hasMore: false,
            totalElements: channelList.length,
          };
          state.channelMetaByChannel[channelId] = {
            ...meta,
            totalElements: (meta.totalElements || channelList.length) + (channelExists ? 0 : 1),
          };
        }
      })
      .addCase(saveMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Unsave message
      .addCase(unsaveMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unsaveMessage.fulfilled, (state, action) => {
        state.loading = false;
        const messageId = action.payload;
        const removed =
          state.savedMessages.find((sm) => sm.messageId === messageId) ||
          Object.values(state.savedMessagesByChannel)
            .flat()
            .find((sm) => sm.messageId === messageId);

        state.savedMessages = state.savedMessages.filter(
          (sm) => sm.messageId !== messageId
        );
        Object.keys(state.savedMessagesByChannel).forEach((channelId) => {
          state.savedMessagesByChannel[channelId] = (
            state.savedMessagesByChannel[channelId] || []
          ).filter((sm) => sm.messageId !== messageId);
          const meta = state.channelMetaByChannel[channelId];
          if (meta) {
            state.channelMetaByChannel[channelId] = {
              ...meta,
              totalElements: Math.max(0, (meta.totalElements || 0) - 1),
            };
          }
        });
        state.savedMessageIds = state.savedMessageIds.filter(
          (id) => id !== messageId
        );

        if (removed?.channelId) {
          state.channelsSummary = state.channelsSummary
            .map((item) =>
              item.channelId === removed.channelId
                ? { ...item, count: Math.max(0, (item.count || 0) - 1) }
                : item
            )
            .filter((item) => item.count > 0);
          if (state.listMeta.totalElements > 0) {
            state.listMeta.totalElements -= 1;
          }
        }
        state.searchResults = state.searchResults.filter(
          (sm) => sm.messageId !== messageId
        );
        if (state.searchMeta.totalElements > 0) {
          state.searchMeta.totalElements -= 1;
        }
      })
      .addCase(unsaveMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch saved messages
      .addCase(fetchSavedMessages.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMore = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchSavedMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        const {
          savedMessages,
          page,
          hasMore,
          totalElements,
          append,
        } = action.payload;

        state.savedMessages = append
          ? [
              ...state.savedMessages,
              ...savedMessages.filter(
                (sm) => !state.savedMessages.some((e) => e.id === sm.id)
              ),
            ]
          : savedMessages;

        state.listMeta = { page, hasMore, totalElements };
        savedMessages.forEach((sm) => {
          if (!state.savedMessageIds.includes(sm.messageId)) {
            state.savedMessageIds.push(sm.messageId);
          }
        });
      })
      .addCase(fetchSavedMessages.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })
      // Fetch channels summary
      .addCase(fetchChannelsSummary.fulfilled, (state, action) => {
        state.channelsSummary = action.payload || [];
      })
      // Search saved messages
      .addCase(searchSavedMessages.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.searchLoadingMore = true;
        } else {
          state.searchLoading = true;
        }
        state.error = null;
      })
      .addCase(searchSavedMessages.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchLoadingMore = false;
        const {
          query,
          channelId,
          savedMessages,
          page,
          hasMore,
          totalElements,
          append,
        } = action.payload;

        state.searchResults = append
          ? [
              ...state.searchResults,
              ...savedMessages.filter(
                (sm) => !state.searchResults.some((e) => e.id === sm.id)
              ),
            ]
          : savedMessages;

        state.searchMeta = {
          query,
          channelId: channelId || null,
          page,
          hasMore,
          totalElements,
        };

        savedMessages.forEach((sm) => {
          if (!state.savedMessageIds.includes(sm.messageId)) {
            state.savedMessageIds.push(sm.messageId);
          }
        });
      })
      .addCase(searchSavedMessages.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchLoadingMore = false;
        state.error = action.payload;
      })
      // Fetch all saved messages
      .addCase(fetchAllSavedMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllSavedMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.savedMessages = action.payload;
        // Update saved message IDs array
        state.savedMessageIds = action.payload.map((sm) => sm.messageId);
      })
      .addCase(fetchAllSavedMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch saved messages by channel
      .addCase(fetchSavedMessagesByChannel.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.channelLoadingMore = true;
        } else {
          state.channelLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchSavedMessagesByChannel.fulfilled, (state, action) => {
        state.channelLoading = false;
        state.channelLoadingMore = false;
        const {
          channelId,
          savedMessages,
          page,
          hasMore,
          totalElements,
          append,
        } = action.payload;

        const existing = state.savedMessagesByChannel[channelId] || [];
        state.savedMessagesByChannel[channelId] = append
          ? [
              ...existing,
              ...savedMessages.filter(
                (sm) => !existing.some((e) => e.messageId === sm.messageId)
              ),
            ]
          : savedMessages;

        state.channelMetaByChannel[channelId] = {
          page,
          hasMore,
          totalElements,
        };

        savedMessages.forEach((sm) => {
          if (!state.savedMessageIds.includes(sm.messageId)) {
            state.savedMessageIds.push(sm.messageId);
          }
        });
      })
      .addCase(fetchSavedMessagesByChannel.rejected, (state, action) => {
        state.channelLoading = false;
        state.channelLoadingMore = false;
        state.error = action.payload;
      })
      // Check if message is saved
      .addCase(checkIfMessageSaved.fulfilled, (state, action) => {
        const { messageId, isSaved } = action.payload;
        if (isSaved) {
          if (!state.savedMessageIds.includes(messageId)) {
            state.savedMessageIds.push(messageId);
          }
        } else {
          state.savedMessageIds = state.savedMessageIds.filter(
            (id) => id !== messageId
          );
        }
      });
  },
});

export const { clearSavedMessages, addSavedMessageId, removeSavedMessageId } =
  savedMessageSlice.actions;

export default savedMessageSlice.reducer;
